import { inject } from '@angular/core';
import type { Route } from '@angular/router';

import { defaultSeoResolver } from '@rpgc-ai/services/seo/default-seo.resolver';
import { LanguageService } from '@rpgc-ai/services/translate/language.service';

import { ECaichatRoute, NOT_FOUND_REASON_CHAT } from '@rpgc-ai/shared-interfaces/enums/routes.enum';
import { EChatbotCreateStatus } from '@rpgc-ai/shared-interfaces/interfaces/chatbot-create.interface';

// Route definitions are required synchronously; moving these legacy exports is outside this lint-only change.
// eslint-disable-next-line @nx/enforce-module-boundaries
import { legacyProfileTabRedirectGuard, MY_PROFILE_CHILD_ROUTES } from '@rpgc-ai/pages/my-profile/my-profile.routes';
import { TOOLS_CHILD_ROUTES } from '@rpgc-ai/pages/tools/constants/tools.routes';

import { AgeConfirmationGuard, authGuard, optionalSessionGuard, tosGuard } from './core/guards/auth.guard';
import { DevelopEnvironmentGuard } from './core/guards/develop-environment.guard';
import { discoverPageGuard } from './core/guards/discover-page.guard';
import { LanguageGuard } from './core/guards/language.guard';
import { chatSeoResolver } from './core/resolvers/chat-seo.resolver';
import { chatbotSeoResolver } from './core/resolvers/chatbot-seo.resolver';
import { publicProfileSeoResolver } from './core/resolvers/public-profile-seo.resolver';
import { seoResolver, translatedTitleResolver } from './core/resolvers/seo.resolver';

const SUBSCRIBE_BACKGROUND = {
  src: 'assets/images/smart-search-backgrounds/6.webp',
} as const;

export const appRoutes: Route[] = [
  {
    path: ':lang/extension-publication-preview',
    canActivate: [LanguageGuard],
    data: { embedded: true },
    loadComponent: () =>
      import('@rpgc-ai/pages/extension-view/extension-publication-preview.component').then((m) => m.ExtensionPublicationPreviewComponent),
  },
  // Маршрут для отображения SVG по адресу /null
  {
    path: 'null',
    loadComponent: () => import('@rpgc-ai/pages/svg-page/svg-page.component').then((m) => m.SvgPageComponent),
  },
  // Embedded preview is deliberately outside the age/auth guarded language
  // shell. Its opaque preview token is accepted only through the parent-origin
  // postMessage handshake; there are no credentials in the URL.
  {
    path: ':lang/chat-extension-lab',
    canActivate: [LanguageGuard],
    data: { embedded: true },
    loadComponent: () => import('@rpgc-ai/layout/chat-extension-lab/chat-extension-lab.component').then((m) => m.ChatExtensionLabComponent),
  },
  {
    path: ':lang/landings/:slug',
    canActivate: [LanguageGuard],
    data: { isLandingSeoOwner: true },
    loadComponent: () => import('@rpgc-ai/pages/public-landing/public-landing.component').then((m) => m.PublicLandingComponent),
  },
  {
    path: ':lang/quiz',
    canActivate: [LanguageGuard],
    resolve: { defaultSeo: defaultSeoResolver },
    loadComponent: () => import('@rpgc-ai/pages/quiz/quiz-screen.component').then((m) => m.QuizScreenComponent),
  },
  {
    path: ':lang',
    canActivate: [LanguageGuard, AgeConfirmationGuard],
    canActivateChild: [AgeConfirmationGuard],
    resolve: { defaultSeo: defaultSeoResolver },
    children: [
      /**
       * Chat
       */
      {
        path: 'chat/:id',
        loadComponent: () => import('@rpgc-ai/layout/chat/chat.component').then((m) => m.ChatComponent),
        resolve: { seo: chatSeoResolver },
        canActivate: [tosGuard],
      },
      /**
       * Extension (mod) page: the author's read-only Screen fills the whole window,
       * so like the chat it lives outside the main layout (no site menu).
       * The page asks a guest to sign in itself and loads after sign-in: authGuard would
       * cancel a link-opened navigation and leave an empty screen at the site root.
       */
      {
        path: 'tools/extension/:id/page',
        data: { title: 'Extension page' },
        loadComponent: () => import('@rpgc-ai/pages/extension-page/extension-page.component').then((m) => m.ExtensionPagePageComponent),
        resolve: { seo: seoResolver },
        canActivate: [tosGuard],
      },
      {
        path: '',
        loadComponent: () => import('@rpgc-ai/layout/main/main.component').then((m) => m.MainComponent),
        children: [
          /**
           * Chatbots catalog
           */
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'catalog',
          },
          // {
          //   path: '',
          //   loadComponent: () => import('@rpgc-ai/pages/home/home.component').then((m) => m.HomeComponent),
          //   canActivate: [optionalSessionGuard],
          //   resolve: { homepage: homepageResolver, seo: seoResolver },
          //   data: {
          //     seo: {
          //       titleKey: 'SEO.HOME_TITLE',
          //       descriptionKey: 'SEO.HOME_DESCRIPTION',
          //     },
          //   },
          // },
          {
            path: 'smart-search',
            loadComponent: () => import('@rpgc-ai/pages/smart-search/smart-search.component').then((m) => m.SmartSearchComponent),
            resolve: { seo: seoResolver },
            data: {
              seo: {
                titleKey: 'SEO.SMART_SEARCH_TITLE',
                descriptionKey: 'SEO.SMART_SEARCH_DESCRIPTION',
                canonicalPath: 'smart-search',
              },
              preload: true,
            },
          },
          {
            path: 'catalog',
            loadComponent: () => import('@rpgc-ai/pages/catalog/catalog.component').then((m) => m.CatalogComponent),
            resolve: { seo: seoResolver },
            canActivate: [tosGuard],
            data: {
              catalogHeaderToolbar: true,
              sitemap: true,
              seo: {
                titleKey: 'SEO.CATALOG_TITLE',
                descriptionKey: 'SEO.CATALOG_DESCRIPTION',
              },
              preload: true,
            },
          },
          {
            path: 'my-profile',
            data: {
              title: 'My profile',
              backLink: true,
              seo: { titleKey: 'SEO.MY_PROFILE_TITLE', descriptionKey: 'SEO.HOME_DESCRIPTION' },
              preload: true,
            },
            loadComponent: () => import('@rpgc-ai/pages/my-profile/my-profile.component').then((m) => m.MyProfileComponent),
            resolve: { seo: seoResolver },
            canActivate: [authGuard, legacyProfileTabRedirectGuard],
            children: MY_PROFILE_CHILD_ROUTES,
          },

          /**
           * Скачивание приложения
           */
          {
            path: 'mobile',
            data: {
              seo: { titleKey: 'SEO.DOWNLOAD_APP_TITLE', descriptionKey: 'SEO.HOME_DESCRIPTION' },
            },
            loadComponent: () => import('@rpgc-ai/pages/mobile/mobile.component').then((m) => m.MobileComponent),
            resolve: { seo: seoResolver },
            canActivate: [optionalSessionGuard],
          },

          /**
           * Создание чатбота
           */
          {
            path: 'create-chatbot',
            data: {
              chatbotStatus: EChatbotCreateStatus.CREATE,
              seo: { titleKey: 'SEO.CREATE_CHATBOT_TITLE', descriptionKey: 'SEO.HOME_DESCRIPTION' },
            },
            loadComponent: () => import('@rpgc-ai/pages/chatbot/chatbot-create.component').then((m) => m.ChatbotCreateComponent),
            resolve: { seo: seoResolver },
            canActivate: [authGuard, tosGuard],
          },
          /**
           * Просмотр и редактирование чатбота
           */
          {
            path: 'view-chatbot/:botId',
            loadComponent: () => import('@rpgc-ai/pages/chatbot/chatbot-create.component').then((m) => m.ChatbotCreateComponent),
            resolve: { seo: chatbotSeoResolver },
            canActivate: [optionalSessionGuard, tosGuard],
          },
          {
            path: 'view-chatbot/:creatorSlug/:botId',
            loadComponent: () => import('@rpgc-ai/pages/chatbot/chatbot-create.component').then((m) => m.ChatbotCreateComponent),
            resolve: { seo: chatbotSeoResolver },
            canActivate: [optionalSessionGuard, tosGuard],
          },
          /**
           * Мои чатботы
           */
          {
            path: 'my-chatbots',
            data: { seo: { titleKey: 'SEO.MY_CHATBOTS_TITLE', descriptionKey: 'SEO.HOME_DESCRIPTION' }, preload: true },
            loadComponent: () => import('@rpgc-ai/pages/my-chatbots/my-chatbots.component').then((m) => m.MyChatbotsComponent),
            resolve: { seo: seoResolver },
            canActivate: [authGuard, tosGuard],
          },
          /**
           * Workshop
           */
          {
            path: 'workshop',
            loadComponent: () => import('@rpgc-ai/pages/workshop/workshop.component').then((m) => m.WorkshopComponent),
            canActivate: [authGuard],
          },
          {
            path: 'workshop/:id',
            loadComponent: () => import('@rpgc-ai/pages/workshop/workshop-game.component').then((m) => m.WorkshopGameComponent),
            canActivate: [authGuard],
          },
          /**
           * Подборка персонажей по одному: свайпы и понравившиеся
           */
          {
            path: 'discover',
            canMatch: [discoverPageGuard],
            data: { seo: { titleKey: 'SEO.DISCOVER_TITLE', descriptionKey: 'SEO.HOME_DESCRIPTION' } },
            loadChildren: () => import('@rpgc-ai/pages/discover/discover.routes').then((m) => m.DISCOVER_ROUTES),
            resolve: { seo: seoResolver },
            canActivate: [authGuard, tosGuard],
          },
          /**
           * Избранные чатботы
           */
          {
            path: 'favorites',
            data: { seo: { titleKey: 'SEO.FAVORITES_TITLE', descriptionKey: 'SEO.HOME_DESCRIPTION' } },
            loadComponent: () => import('@rpgc-ai/pages/favorites/favorites.component').then((m) => m.FavoritesComponent),
            resolve: { seo: seoResolver },
            canActivate: [authGuard, tosGuard],
          },
          /**
           * Leaderboard
           */
          {
            path: 'leaderboard',
            data: {
              title: 'Leaderboard',
              backLink: true,
              fullBleed: true,
              fullBleedBackground: {
                src: 'assets/leaderboard.webp',
                srcset: 'assets/leaderboard.webp 1x, assets/leaderboard@2x.webp 2x',
              },
            },
            loadComponent: () => import('@rpgc-ai/pages/leaderboard/leaderboard.component').then((m) => m.LeaderboardComponent),
            resolve: { seo: seoResolver },
            canActivate: [tosGuard],
          },

          /**
           * История чатов
           */
          {
            path: 'chat-history',
            data: {
              title: 'Chat History',
              backLink: true,
              seo: { titleKey: 'SEO.CHAT_HISTORY_TITLE', descriptionKey: 'SEO.HOME_DESCRIPTION' },
              preload: true,
            },
            loadComponent: () => import('@rpgc-ai/pages/chat-history/chat-history.component').then((m) => m.ChatHistoryComponent),
            resolve: { seo: seoResolver },
            canActivate: [authGuard, tosGuard],
          },
          /** Models and Prompts view and create page */
          {
            path: `${ECaichatRoute.TOOLS}`,
            data: { title: 'Tools', backLink: true },
            loadComponent: () => import('@rpgc-ai/pages/tools/tools.component').then((m) => m.ToolsPageComponent),
            resolve: { seo: seoResolver },
            canActivate: [authGuard, tosGuard],
            children: TOOLS_CHILD_ROUTES,
          },
          /** Model view page */
          {
            path: `${ECaichatRoute.TOOLS}/${ECaichatRoute.MODELS}/:id`,
            data: { title: 'View model', backLink: true },
            loadComponent: () => import('@rpgc-ai/pages/model-view/model-view.component').then((m) => m.ModelViewPageComponent),
            resolve: { seo: seoResolver },
            canActivate: [authGuard, tosGuard],
          },
          /** Extension create page */
          {
            path: 'tools/extension/create',
            data: { title: 'Create extension', backLink: true },
            loadComponent: () =>
              import('@rpgc-ai/pages/extension-create/extension-create.component').then((m) => m.ExtensionCreatePageComponent),
            resolve: { seo: seoResolver },
            canActivate: [authGuard, tosGuard],
          },
          /** Extension edit page */
          {
            path: 'tools/extension/:id/edit',
            data: { title: 'Edit extension', backLink: true },
            loadComponent: () =>
              import('@rpgc-ai/pages/extension-create/extension-create.component').then((m) => m.ExtensionCreatePageComponent),
            resolve: { seo: seoResolver },
            canActivate: [authGuard, tosGuard],
          },
          /** Extension view page */
          {
            path: 'tools/extension/:id',
            data: { title: 'View extension', backLink: true },
            loadComponent: () => import('@rpgc-ai/pages/extension-view/extension-view.component').then((m) => m.ExtensionViewPageComponent),
            resolve: { seo: seoResolver },
            canActivate: [authGuard, tosGuard],
          },
          /** Prompt view page */
          {
            path: `${ECaichatRoute.TOOLS}/${ECaichatRoute.PROMPTS}/:id`,
            data: { title: 'View prompt', backLink: true },
            loadComponent: () => import('@rpgc-ai/pages/prompt-view/prompt-view.component').then((m) => m.PromptViewPageComponent),
            resolve: { seo: seoResolver },
            canActivate: [authGuard, tosGuard],
          },
          /** Prompt edit page */
          {
            path: 'tools/prompt-constructor/:id',
            data: { title: 'Edit prompt', backLink: true },
            loadComponent: () =>
              import('@rpgc-ai/pages/prompt-constructor/prompt-constructor.component').then((m) => m.PromptConstructorComponent),
            resolve: { seo: seoResolver },
            canActivate: [authGuard, tosGuard],
          },
          /** Prompt create page */
          {
            path: 'tools/prompt-constructor',
            data: { title: 'Create prompt', backLink: true },
            loadComponent: () =>
              import('@rpgc-ai/pages/prompt-constructor/prompt-constructor.component').then((m) => m.PromptConstructorComponent),
            resolve: { seo: seoResolver },
            canActivate: [authGuard, tosGuard],
          },
          /**
           * Мои персонажи
           */
          {
            path: 'my-personas',
            data: {
              title: 'My Personas',
              backLink: true,
              seo: { titleKey: 'SEO.MY_PERSONAS_TITLE', descriptionKey: 'SEO.HOME_DESCRIPTION' },
            },
            loadComponent: () => import('@rpgc-ai/pages/my-personas/my-personas.component').then((m) => m.MyPersonasComponent),
            resolve: { seo: seoResolver },
            canActivate: [authGuard, tosGuard],
          },
          {
            path: 'create-persona',
            data: {
              title: 'Create Persona',
              backLink: true,
              seo: { titleKey: 'SEO.CREATE_PERSONA_TITLE', descriptionKey: 'SEO.HOME_DESCRIPTION' },
            },
            loadComponent: () =>
              import('@rpgc-ai/pages/create-persona-page/create-persona-page.component').then((m) => m.CreatePersonaPageComponent),
            resolve: { seo: seoResolver },
            canActivate: [authGuard, tosGuard],
          },
          {
            path: 'edit-persona/:id',
            data: {
              title: 'Edit Persona',
              backLink: true,
              seo: { titleKey: 'SEO.EDIT_PERSONA_TITLE', descriptionKey: 'SEO.HOME_DESCRIPTION' },
            },
            loadComponent: () =>
              import('@rpgc-ai/pages/create-persona-page/create-persona-page.component').then((m) => m.CreatePersonaPageComponent),
            resolve: { seo: seoResolver },
            canActivate: [authGuard, tosGuard],
          },
          {
            path: ECaichatRoute.SUBSCRIBE_PAGE,
            data: {
              title: 'Subscribe',
              backLink: true,
              fullBleedBackground: SUBSCRIBE_BACKGROUND,
              seo: {
                titleKey: 'SEO.SUBSCRIBE_TITLE',
                descriptionKey: 'SEO.SUBSCRIBE_DESCRIPTION',
              },
              preload: true,
            },
            loadComponent: () => import('@rpgc-ai/pages/subscribe-page/subscribe-page.component').then((m) => m.SubscribePageComponent),
            resolve: { seo: seoResolver },
          },
          {
            path: 'subscribe/:section',
            data: { title: 'Subscribe', backLink: true, fullBleedBackground: SUBSCRIBE_BACKGROUND, preload: true },
            loadComponent: () => import('@rpgc-ai/pages/subscribe-page/subscribe-page.component').then((m) => m.SubscribePageComponent),
            resolve: { seo: seoResolver },
          },
          {
            path: 'feed',
            data: {
              title: 'Feed',
              // TODO: need seo text for feed page
              // seo: {
              //   titleKey: 'SEO.SUBSCRIBE_TITLE',
              //   descriptionKey: 'SEO.SUBSCRIBE_DESCRIPTION',
              // },
            },
            loadComponent: () => import('@rpgc-ai/pages/feed/feed.component').then((m) => m.FeedComponent),
            resolve: { seo: seoResolver },
          },
          // TODO: hide until help page refactor is complete
          // {
          //   path: 'help',
          //   data: {
          //     title: 'Help',
          //     seo: {
          //       titleKey: 'SEO.HELP_TITLE',
          //       descriptionKey: 'SEO.HELP_DESCRIPTION',
          //     },
          //   },
          //   loadChildren: () => import('@rpgc-ai/pages/help/help.routes').then((r) => r.helpRoutes),
          //   resolve: { seo: seoResolver },
          // },
          {
            path: 'profile/:id',
            data: { title: 'User profile', backLink: true },
            loadComponent: () => import('@rpgc-ai/pages/public-profile/public-profile.component').then((m) => m.PublicProfileComponent),
            resolve: { seo: publicProfileSeoResolver },
          },
          {
            path: 'need-auth',
            loadComponent: () =>
              import('@rpgc-ai/lazy-blocks/do-you-need-auth/do-you-need-auth.component').then((m) => m.DoYouNeedAuthComponent),
            resolve: { seo: seoResolver },
          },
          {
            path: 'terms-and-conditions',
            data: {
              title: 'Terms & Conditions',
              backLink: true,
              seo: {
                titleKey: 'SEO.TERMS_TITLE',
                descriptionKey: 'SEO.TERMS_DESCRIPTION',
              },
            },
            loadComponent: () =>
              import('@rpgc-ai/pages/terms-and-conditions/terms-and-conditions.component').then((m) => m.TermsAndConditionsComponent),
            resolve: { seo: seoResolver },
          },
          {
            path: 'privacy-policy',
            data: {
              title: 'Privacy Policy',
              backLink: true,
              seo: {
                titleKey: 'SEO.PRIVACY_TITLE',
                descriptionKey: 'SEO.PRIVACY_DESCRIPTION',
              },
            },
            loadComponent: () => import('@rpgc-ai/pages/privacy-policy/privacy-policy.component').then((m) => m.PrivacyPolicyComponent),
            resolve: { seo: seoResolver },
          },
          {
            path: 'refund-policy',
            data: {
              title: 'Privacy Policy',
              backLink: true,
              seo: {
                titleKey: 'SEO.REFUND_TITLE',
                descriptionKey: 'SEO.REFUND_DESCRIPTION',
              },
            },
            resolve: { seo: seoResolver },
            loadComponent: () => import('@rpgc-ai/pages/refund-policy/refund-policy.component').then((m) => m.RefundPolicyComponent),
          },
          {
            path: 'languages-admin',
            data: { title: 'Languages Admin', backLink: true },
            loadComponent: () => import('@rpgc-ai/pages/languages-admin/languages-admin.component').then((m) => m.LanguagesAdminComponent),
            resolve: { seo: seoResolver },
            canActivate: [DevelopEnvironmentGuard],
          },
          {
            path: 'blocked-content-policy',
            data: {
              seo: {
                titleKey: 'SEO.BLOCKED_CONTENT_TITLE',
                descriptionKey: 'SEO.BLOCKED_CONTENT_DESCRIPTION',
              },
            },
            loadComponent: () =>
              import('@rpgc-ai/pages/blocked-content-policy/blocked-content-policy.component').then((m) => m.BlockedContentPolicyComponent),
            resolve: { seo: seoResolver },
          },
          {
            path: 'community-guidelines',
            data: {
              seo: {
                titleKey: 'SEO.COMMUNITY_TITLE',
                descriptionKey: 'SEO.COMMUNITY_DESCRIPTION',
              },
            },
            loadComponent: () =>
              import('@rpgc-ai/pages/community-guidelines/community-guidelines.component').then((m) => m.CommunityGuidelinesComponent),
            resolve: { seo: seoResolver },
          },
          {
            path: 'content-removal-policy',
            data: {
              seo: {
                titleKey: 'SEO.CONTENT_REMOVAL_TITLE',
                descriptionKey: 'SEO.CONTENT_REMOVAL_DESCRIPTION',
              },
            },
            loadComponent: () =>
              import('@rpgc-ai/pages/content-removal-policy/content-removal-policy.component').then((m) => m.ContentRemovalPolicyComponent),
            resolve: { seo: seoResolver },
          },
          {
            path: 'underage-policy',
            data: {
              seo: {
                titleKey: 'SEO.UNDERAGE_TITLE',
                descriptionKey: 'SEO.UNDERAGE_DESCRIPTION',
              },
            },
            loadComponent: () => import('@rpgc-ai/pages/underage-policy/underage-policy.component').then((m) => m.UnderagePolicyComponent),
            resolve: { seo: seoResolver },
          },
          {
            path: 'ai-safety-and-monitoring-policy',
            // data: {
            //   seo: {
            //     titleKey: 'SEO.UNDERAGE_TITLE',
            //     descriptionKey: 'SEO.UNDERAGE_DESCRIPTION',
            //   },
            // },
            loadComponent: () =>
              import('@rpgc-ai/pages/ai-safety-and-monitoring-policy/ai-safety-and-monitoring-policy.component').then(
                (m) => m.AISafetyPolicyComponent,
              ),
            resolve: { seo: seoResolver },
          },
          {
            path: 'payment/:status/:id',
            loadComponent: () => import('@rpgc-ai/pages/payment/payment.component').then((m) => m.PaymentComponent),
            resolve: { seo: seoResolver },
          },

          /**
           * 404
           */
          {
            path: ECaichatRoute.NOT_FOUND,
            data: { title: 'Page Not Found' },
            loadComponent: () => import('@rpgc-ai/pages/not-found/not-found.component').then((m) => m.NotFoundComponent),
          },
          /** Цель редиректа для несуществующего чата: тот же экран, но с формулировками про чат. */
          {
            path: `${ECaichatRoute.NOT_FOUND}/${NOT_FOUND_REASON_CHAT}`,
            title: translatedTitleResolver('PAGES.NOT_FOUND.CHAT_TITLE'),
            data: {
              notFoundReason: NOT_FOUND_REASON_CHAT,
              seo: {
                titleKey: 'PAGES.NOT_FOUND.CHAT_TITLE',
                descriptionKey: 'PAGES.NOT_FOUND.CHAT_DESCRIPTION',
              },
            },
            loadComponent: () => import('@rpgc-ai/pages/not-found/not-found.component').then((m) => m.NotFoundComponent),
            resolve: { seo: seoResolver },
          },
          {
            path: '**',
            data: { title: 'Page Not Found' },
            loadComponent: () => import('@rpgc-ai/pages/not-found/not-found.component').then((m) => m.NotFoundComponent),
          },
        ],
      },
    ],
  },
  {
    path: '',
    pathMatch: 'full',
    redirectTo: () => {
      const languageService = inject(LanguageService);
      return languageService.initLanguage();
    },
  },
];
